import React, { useState, useEffect } from 'react';
import {
  Accordion, Table, Badge, Form, Row, Col, Button, Card, Alert, Modal, ProgressBar
} from 'react-bootstrap';
import { FaLock, FaShieldAlt, FaKey, FaCheck, FaTimes, FaPlus, FaEdit } from 'react-icons/fa';
import axios from 'axios';

const GDPRSecurityEncryptionTab= ({ userData, onScoreUpdate, showNotification, region, refreshActivities }) => {
  const [securityMeasures, setSecurityMeasures] = useState([]);
  const [encryptionStatus, setEncryptionStatus] = useState({});
  const [accessControls, setAccessControls] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newMeasure, setNewMeasure] = useState({
    category: '',
    measure: '',
    implementation: 'Implemented',
    description: '',
    responsibleParty: '',
    reviewDate: '',
    gdprArticle: ''
  });
  const [showAccessModal, setShowAccessModal] = useState(false);
const [newAccessControl, setNewAccessControl] = useState({
  type: '',
  description: '',
  status: 'Active',
  coverage: 0
});
const handleSaveAccessControl = async () => {
  try {
    await axios.post('https://njg3580k81.execute-api.ap-southeast-2.amazonaws.com/prod/gdpr/access-controls/create', newAccessControl, { headers });
    await logActivity(`Added access control: ${newAccessControl.type}`, "success");
    showNotification('Access control added successfully!');
    fetchSecurityData();
    resetAccessForm();
  } catch (error) {
    console.error('Error saving access control:', error);
    showNotification('Error saving access control', 'danger');
  }
};

const resetAccessForm = () => {
  setNewAccessControl({
    type: '',
    description: '',
    status: 'Active',
    coverage: 0
  });
  setShowAccessModal(false);
};


  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'] 
  };

  const SECURITY_CATEGORIES = [
    'Technical Safeguards',
    'Organizational Measures',
    'Physical Security',
    'Access Controls',
    'Data Encryption',
    'Network Security',
    'Incident Response',
    'Staff Training',
    'Vendor Management',
    'Business Continuity'
  ];

  const GDPR_ARTICLES = [
    'Art. 32 - Security of processing',
    'Art. 25 - Data protection by design',
    'Art. 28 - Processor obligations',
    'Art. 33 - Breach notification to SA',
    'Art. 34 - Breach notification to data subject'
  ];

  useEffect(() => {
    fetchSecurityData();
  }, []);

const fetchSecurityData = async () => {
  try {
    // Save encryption status first
    await axios.post("https://i801s21mfh.execute-api.ap-southeast-2.amazonaws.com/prod/gdpr/encryption-status/save", {
      dataAtRestEncrypted: true,
      atRestAlgorithm: "AES-256",
      dataInTransitEncrypted: true,
      inTransitProtocol: "TLS 1.3"
    }, { headers });

    // Fetch correct data from proper endpoints
    const [measuresRes, encryptionRes, accessRes] = await Promise.all([
      axios.get('https://niwiorih16.execute-api.ap-southeast-2.amazonaws.com/prod/gdpr/security-measures/list', { headers }), // ✅ Correct endpoint
      axios.get('https://b0jc8j4xf8.execute-api.ap-southeast-2.amazonaws.com/prod/gdpr/encryption-status', { headers }),
      axios.get('https://y2z9653kz0.execute-api.ap-southeast-2.amazonaws.com/prod/gdpr/access-controls/list', { headers })
    ]);

    setSecurityMeasures(measuresRes.data);
    setEncryptionStatus(encryptionRes.data);
    setAccessControls(accessRes.data);
    
    calculateSecurityScore(measuresRes.data, encryptionRes.data, accessRes.data);
  } catch (error) {
    console.error('Error fetching security data:', error);
  }
};


  const calculateSecurityScore = (measures, encryption, access) => {
    let score = 0;
    
    // Technical Measures (40% of score)
    const implementedMeasures = measures.filter(m => m.implementation === 'Implemented').length;
    const totalMeasures = measures.length || 1;
    const technicalScore = (implementedMeasures / totalMeasures) * 40;
    
    // Encryption Coverage (35% of score)
    const encryptionCoverage = encryption.dataAtRestEncrypted && encryption.dataInTransitEncrypted ? 35 : 
                              (encryption.dataAtRestEncrypted || encryption.dataInTransitEncrypted) ? 17.5 : 0;
    
    // Access Controls (25% of score)
    const rbacImplemented = access.some(a => a.type === 'Role-Based Access Control');
    const mfaImplemented = access.some(a => a.type === 'Multi-Factor Authentication');
    const accessScore = (rbacImplemented ? 12.5 : 0) + (mfaImplemented ? 12.5 : 0);
    
    score = Math.round(technicalScore + encryptionCoverage + accessScore);
    onScoreUpdate(Math.min(100, Math.max(0, score)));
  };

const handleSaveMeasure = async () => {
  try {
    await axios.post('https://t4fgp2ssv8.execute-api.ap-southeast-2.amazonaws.com/prod/gdpr/security-measures/create', newMeasure, { headers }); // ✅ Correct endpoint
    await logActivity(`Added security measure: ${newMeasure.measure}`, "success");
    showNotification('Security measure added successfully!');
    fetchSecurityData();
    resetForm();
  } catch (error) {
    console.error('Error saving security measure:', error);
    showNotification('Error saving security measure', 'danger');
  }
};


  const resetForm = () => {
    setNewMeasure({
      category: '',
      measure: '',
      implementation: 'Implemented',
      description: '',
      responsibleParty: '',
      reviewDate: '',
      gdprArticle: ''
    });
    setShowModal(false);
  };

const logActivity = async (message, badge = "info") => {

  
  try {
    // ✅ Map frontend fields to backend entity fields
    const payload = {
      action: badge,                    // badge → action
      module: "Security/Encryption",   // Add missing module field
      details: message                  // message → details
      // Remove timestamp - let backend set it
    };
    
    
    const response = await axios.post(
      "https://w8wvx2z3f4.execute-api.ap-southeast-2.amazonaws.com/prod/activity-log/save", 
      payload,     
      { headers }  
    );
    
    if (refreshActivities) {
        await refreshActivities();
      }
    
  } catch (err) {
    console.error('❌ Error in logActivity:', err);
    
    if (err.response) {
      console.error('🔥 Server Error Details:');
      console.error('Status:', err.response.status);
      console.error('Data:', err.response.data);
    }
    
    console.warn("Failed to log activity:", err.message);
  }
};

const [activeAccordionKey, setActiveAccordionKey] = useState(null);


  return (
    <>
      <div className="border rounded">
  <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "0" ? null : "0")
    }
  >
    <h6>Security Measures Overview</h6>
    <span>{activeAccordionKey === "0" ? "▲" : "▼"}</span>
  </div>

  {activeAccordionKey === "0" && (
    <div className="p-3 border-top">
            <Row className="mb-3">
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4 className="text-primary">{securityMeasures.filter(m => m.implementation === 'Implemented').length}</h4>
                    <small>Implemented</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4 className="text-warning">{securityMeasures.filter(m => m.implementation === 'In Progress').length}</h4>
                    <small>In Progress</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4 className="text-danger">{securityMeasures.filter(m => m.implementation === 'Not Implemented').length}</h4>
                    <small>Not Implemented</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4>{securityMeasures.length}</h4>
                    <small>Total Measures</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Button variant="primary" className="mb-3" onClick={() => setShowModal(true)}>
              <FaPlus /> Add Security Measure
            </Button>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Security Measure</th>
                  <th>Implementation Status</th>
                  <th>Responsible Party</th>
                  <th>GDPR Article</th>
                  <th>Review Date</th>
                </tr>
              </thead>
              <tbody>
                {securityMeasures.map((measure, index) => (
                  <tr key={index}>
                    <td>{measure.category}</td>
                    <td>{measure.measure}</td>
                    <td>
                      <Badge bg={
                        measure.implementation === 'Implemented' ? 'success' :
                        measure.implementation === 'In Progress' ? 'warning' : 'danger'
                      }>
                        {measure.implementation}
                      </Badge>
                    </td>
                    <td>{measure.responsibleParty}</td>
                    <td>{measure.gdprArticle}</td>
                    <td>{measure.reviewDate}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
  )}

       <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "1" ? null : "1")
    }
  >
    <h6>Encryption Status</h6>
    <span>{activeAccordionKey === "1" ? "▲" : "▼"}</span>
  </div>

  {activeAccordionKey === "1" && (
    <div className="p-3 border-top">
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <Card.Title>Data at Rest Encryption</Card.Title>
                    <div className="d-flex align-items-center">
                      {encryptionStatus.dataAtRestEncrypted ? 
                        <FaCheck className="text-success me-2" /> : 
                        <FaTimes className="text-danger me-2" />
                      }
                      <span>{encryptionStatus.dataAtRestEncrypted ? 'Enabled' : 'Not Enabled'}</span>
                    </div>
                    <small className="text-muted">Algorithm: {encryptionStatus.atRestAlgorithm || 'Not specified'}</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <Card.Title>Data in Transit Encryption</Card.Title>
                    <div className="d-flex align-items-center">
                      {encryptionStatus.dataInTransitEncrypted ? 
                        <FaCheck className="text-success me-2" /> : 
                        <FaTimes className="text-danger me-2" />
                      }
                      <span>{encryptionStatus.dataInTransitEncrypted ? 'Enabled' : 'Not Enabled'}</span>
                    </div>
                    <small className="text-muted">Protocol: {encryptionStatus.inTransitProtocol || 'Not specified'}</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Alert variant="info" className="mt-3">
              <strong>GDPR Requirement:</strong> Article 32 requires appropriate technical measures including encryption 
              of personal data to ensure security appropriate to the risk.
            </Alert>
          </div>
  )}

        <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "2" ? null : "2")
    }
  >
    <h6>Access Controls</h6>
    <span>{activeAccordionKey === "2" ? "▲" : "▼"}</span>
  </div>

  {activeAccordionKey === "2" && (
    <div className="p-3 border-top">
     <Button variant="primary" className="mb-3" onClick={() => setShowAccessModal(true)}>
      <FaPlus /> Add Access Control
      </Button>

           <Table striped bordered hover responsive>

              <thead>
                <tr>
                  <th>Control Type</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Coverage</th>
                </tr>
              </thead>
              <tbody>
                {accessControls.map((control, index) => (
                  <tr key={index}>
                    <td>{control.type}</td>
                    <td>{control.description}</td>
                    <td>
                      <Badge bg={control.status === 'Active' ? 'success' : 'warning'}>
                        {control.status}
                      </Badge>
                    </td>
                    <td>
                      <ProgressBar 
                        now={control.coverage} 
                        label={`${control.coverage}%`}
                        variant={control.coverage >= 80 ? 'success' : control.coverage >= 50 ? 'warning' : 'danger'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
  )}
      </div>

      <Modal show={showModal} onHide={resetForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Security Measure</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={newMeasure.category}
                    onChange={(e) => setNewMeasure({...newMeasure, category: e.target.value})}
                  >
                    <option value="">Select Category</option>
                    {SECURITY_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Implementation Status</Form.Label>
                  <Form.Select
                    value={newMeasure.implementation}
                    onChange={(e) => setNewMeasure({...newMeasure, implementation: e.target.value})}
                  >
                    <option value="Implemented">Implemented</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Not Implemented">Not Implemented</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Security Measure</Form.Label>
              <Form.Control
                type="text"
                value={newMeasure.measure}
                onChange={(e) => setNewMeasure({...newMeasure, measure: e.target.value})}
                placeholder="e.g., Multi-factor authentication for admin access"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newMeasure.description}
                onChange={(e) => setNewMeasure({...newMeasure, description: e.target.value})}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Responsible Party</Form.Label>
                  <Form.Control
                    type="text"
                    value={newMeasure.responsibleParty}
                    onChange={(e) => setNewMeasure({...newMeasure, responsibleParty: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Review Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newMeasure.reviewDate}
                    onChange={(e) => setNewMeasure({...newMeasure, reviewDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Related GDPR Article</Form.Label>
              <Form.Select
                value={newMeasure.gdprArticle}
                onChange={(e) => setNewMeasure({...newMeasure, gdprArticle: e.target.value})}
              >
                <option value="">Select Article</option>
                {GDPR_ARTICLES.map(article => (
                  <option key={article} value={article}>{article}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetForm}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveMeasure}>Save Measure</Button>
        </Modal.Footer>
      </Modal>
      {/* Access Control Modal */}
<Modal show={showAccessModal} onHide={resetAccessForm} size="lg">
  <Modal.Header closeButton>
    <Modal.Title>Add Access Control</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <Form>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Control Type</Form.Label>
            <Form.Select
              value={newAccessControl.type}
              onChange={(e) => setNewAccessControl({...newAccessControl, type: e.target.value})}
            >
              <option value="">Select Control Type</option>
              <option value="Role-Based Access Control">Role-Based Access Control</option>
              <option value="Multi-Factor Authentication">Multi-Factor Authentication</option>
              <option value="Privileged Access Management">Privileged Access Management</option>
              <option value="Single Sign-On">Single Sign-On</option>
              <option value="Identity Governance">Identity Governance</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={newAccessControl.status}
              onChange={(e) => setNewAccessControl({...newAccessControl, status: e.target.value})}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Pending">Pending</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={newAccessControl.description}
          onChange={(e) => setNewAccessControl({...newAccessControl, description: e.target.value})}
          placeholder="Describe the access control implementation"
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Coverage Percentage</Form.Label>
        <Form.Control
          type="number"
          min="0"
          max="100"
          value={newAccessControl.coverage}
          onChange={(e) => setNewAccessControl({...newAccessControl, coverage: parseInt(e.target.value) || 0})}
          placeholder="0-100"
        />
      </Form.Group>
    </Form>
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={resetAccessForm}>Cancel</Button>
    <Button variant="primary" onClick={handleSaveAccessControl}>Save Access Control</Button>
  </Modal.Footer>
</Modal>

    </>
  );
};

export default GDPRSecurityEncryptionTab;
